import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, CheckCircle, Clock, AlertTriangle, XCircle, 
  FileText, Settings, History, RefreshCw, Check, X,
  Users, Shield, Bell, FileOutput
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';

export default function DashboardF8125() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({ periodos: [], config: {} });
  const [auditoria, setAuditoria] = useState([]);
  const [config, setConfig] = useState({
    dia_vencimiento: 20,
    dias_alerta_previa: 5,
    mensaje_movimientos: 'Los movimientos de @periodo están listos para procesar',
    mensaje_padron: 'El padrón de @periodo está actualizado',
    mensaje_completo: 'El período @periodo está completo y listo para generar reportes'
  });
  const [permisos, setPermisos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  useEffect(() => {
    loadDashboard();
    loadAuditoria();
    loadConfig();
    loadPermisos();
    loadUsuarios();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/cierre-periodo/dashboard');
      setDashboard(res.data);
    } catch (error) {
      toast.error('Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditoria = async () => {
    try {
      const res = await api.get('/cierre-periodo/auditoria?limit=50');
      setAuditoria(res.data);
    } catch (error) {
      console.error('Error loading auditoria');
    }
  };

  const loadConfig = async () => {
    try {
      const res = await api.get('/cierre-periodo/config');
      setConfig(res.data);
    } catch (error) {
      console.error('Error loading config');
    }
  };

  const loadPermisos = async () => {
    try {
      const res = await api.get('/cierre-periodo/permisos');
      setPermisos(res.data);
    } catch (error) {
      console.error('Error loading permisos');
    }
  };

  const loadUsuarios = async () => {
    try {
      const res = await api.get('/users');
      setUsuarios(res.data.users || res.data || []);
    } catch (error) {
      console.error('Error loading usuarios');
    }
  };

  const handleMarcarCierre = async (periodo, tipo, completo) => {
    try {
      await api.post('/cierre-periodo/marcar', {
        periodo,
        tipo,
        completo,
        comentarios: completo ? `Marcado como completo` : 'Reabierto'
      });
      toast.success(`${tipo} ${completo ? 'marcado como completo' : 'reabierto'}`);
      loadDashboard();
      loadAuditoria();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleMarcarPresentado = async (reporteId) => {
    const numero = prompt('Número de presentación AFIP (opcional):');
    try {
      await api.post('/cierre-periodo/marcar-presentado', {
        reporte_id: reporteId,
        numero_presentacion: numero || null
      });
      toast.success('Reporte marcado como presentado');
      loadDashboard();
      loadAuditoria();
    } catch (error) {
      toast.error('Error al marcar como presentado');
    }
  };

  const handleSaveConfig = async () => {
    try {
      await api.put('/cierre-periodo/config', config);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const handleTogglePermiso = async (userId, tienePermiso) => {
    try {
      if (tienePermiso) {
        await api.delete(`/cierre-periodo/permisos/${userId}`);
      } else {
        await api.post(`/cierre-periodo/permisos/${userId}`);
      }
      toast.success('Permiso actualizado');
      loadPermisos();
    } catch (error) {
      toast.error('Error al actualizar permiso');
    }
  };

  const formatPeriodo = (periodo) => {
    if (!periodo || periodo.length !== 6) return periodo;
    const año = periodo.substring(0, 4);
    const mes = parseInt(periodo.substring(4, 6));
    const meses = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${meses[mes]} ${año}`;
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      presentado: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      generado: { bg: 'bg-blue-100', text: 'text-blue-800', icon: FileText },
      listo: { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: Check },
      pendiente: { bg: 'bg-slate-100', text: 'text-slate-600', icon: Clock },
      por_vencer: { bg: 'bg-amber-100', text: 'text-amber-800', icon: AlertTriangle },
      vencido: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle }
    };
    const estilo = estilos[estado] || estilos.pendiente;
    const Icon = estilo.icon;
    
    return (
      <Badge className={`${estilo.bg} ${estilo.text} flex items-center gap-1`}>
        <Icon size={12} />
        {estado.replace('_', ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="animate-spin mr-2" />
        Cargando...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard F8125</h1>
          <p className="text-slate-500">Estado de presentaciones y cierre de períodos</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('../f8125')}>
            <FileOutput size={16} className="mr-2" />
            Generar F8125
          </Button>
          <Button onClick={loadDashboard} variant="outline">
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <Calendar size={16} />
            Estado Períodos
          </TabsTrigger>
          <TabsTrigger value="auditoria" className="flex items-center gap-2">
            <History size={16} />
            Auditoría
          </TabsTrigger>
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings size={16} />
            Configuración
          </TabsTrigger>
          <TabsTrigger value="permisos" className="flex items-center gap-2">
            <Shield size={16} />
            Permisos
          </TabsTrigger>
        </TabsList>

        {/* Tab: Dashboard */}
        <TabsContent value="dashboard">
          <Card className="p-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Período</TableHead>
                  <TableHead>Vencimiento</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-center">Movimientos</TableHead>
                  <TableHead className="text-center">Padrón</TableHead>
                  <TableHead className="text-center">Reportes</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.periodos.map((p, idx) => (
                  <TableRow key={idx} className={p.estado === 'vencido' ? 'bg-red-50' : p.estado === 'por_vencer' ? 'bg-amber-50' : ''}>
                    <TableCell className="font-medium">{formatPeriodo(p.periodo)}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {new Date(p.fecha_vencimiento).toLocaleDateString('es-AR')}
                        <div className={`text-xs ${p.dias_para_vencer < 0 ? 'text-red-600' : p.dias_para_vencer <= 5 ? 'text-amber-600' : 'text-slate-500'}`}>
                          {p.dias_para_vencer < 0 ? `Vencido hace ${Math.abs(p.dias_para_vencer)} días` : `En ${p.dias_para_vencer} días`}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getEstadoBadge(p.estado)}</TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={() => handleMarcarCierre(p.periodo, 'movimientos', !p.cierre.movimientos_completo)}
                        className={`p-2 rounded-full transition-colors ${
                          p.cierre.movimientos_completo 
                            ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                            : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                        }`}
                        title={p.cierre.movimientos_completo ? 'Marcar como incompleto' : 'Marcar como completo'}
                      >
                        {p.cierre.movimientos_completo ? <CheckCircle size={20} /> : <Clock size={20} />}
                      </button>
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={() => handleMarcarCierre(p.periodo, 'padron', !p.cierre.padron_completo)}
                        className={`p-2 rounded-full transition-colors ${
                          p.cierre.padron_completo 
                            ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                            : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                        }`}
                        title={p.cierre.padron_completo ? 'Marcar como incompleto' : 'Marcar como completo'}
                      >
                        {p.cierre.padron_completo ? <CheckCircle size={20} /> : <Clock size={20} />}
                      </button>
                    </TableCell>
                    <TableCell className="text-center">
                      {p.reportes_generados > 0 ? (
                        <Badge className="bg-blue-100 text-blue-800">{p.reportes_generados}</Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {p.ultimo_reporte && !p.presentado && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarcarPresentado(p.ultimo_reporte.id)}
                            className="text-green-600"
                          >
                            <Check size={14} className="mr-1" />
                            Presentado
                          </Button>
                        )}
                        {p.presentado && (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle size={12} className="mr-1" />
                            Presentado
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Leyenda */}
          <Card className="p-4">
            <h3 className="font-medium mb-3">Leyenda</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle size={12} className="text-green-600" />
                </div>
                <span>Presentado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText size={12} className="text-blue-600" />
                </div>
                <span>Generado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check size={12} className="text-emerald-600" />
                </div>
                <span>Datos listos</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle size={12} className="text-amber-600" />
                </div>
                <span>Por vencer</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle size={12} className="text-red-600" />
                </div>
                <span>Vencido</span>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Tab: Auditoría */}
        <TabsContent value="auditoria">
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Log de Auditoría</h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Acción</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Comentarios</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditoria.map((log, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-sm">
                      {log.fecha ? new Date(log.fecha).toLocaleString('es-AR') : '-'}
                    </TableCell>
                    <TableCell className="font-mono">{formatPeriodo(log.periodo)}</TableCell>
                    <TableCell>
                      <Badge className={log.accion === 'CERRADO' || log.accion === 'PRESENTADO' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                        {log.accion}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm capitalize">{log.tipo || log.origen}</TableCell>
                    <TableCell className="text-sm">{log.usuario}</TableCell>
                    <TableCell className="text-sm text-slate-500">{log.comentarios || '-'}</TableCell>
                  </TableRow>
                ))}
                {auditoria.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                      No hay registros de auditoría
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Tab: Configuración */}
        <TabsContent value="config">
          <Card className="p-6 space-y-6">
            <h2 className="text-lg font-semibold">Configuración de Cierre de Período</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Día de Vencimiento F8125</Label>
                <Input
                  type="number"
                  value={config.dia_vencimiento}
                  onChange={(e) => setConfig({ ...config, dia_vencimiento: parseInt(e.target.value) || 20 })}
                  min={1}
                  max={31}
                />
                <p className="text-xs text-slate-500 mt-1">Día del mes siguiente en que vence la presentación</p>
              </div>
              
              <div>
                <Label>Días de Alerta Previa</Label>
                <Input
                  type="number"
                  value={config.dias_alerta_previa}
                  onChange={(e) => setConfig({ ...config, dias_alerta_previa: parseInt(e.target.value) || 5 })}
                  min={1}
                  max={15}
                />
                <p className="text-xs text-slate-500 mt-1">Cuántos días antes del vencimiento alertar</p>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                <Bell size={18} />
                Mensajes de Notificación
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Usa <code className="bg-slate-100 px-1 rounded">@periodo</code> para insertar el período (ej: "Enero 2025")
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label>Mensaje: Movimientos Completos</Label>
                  <Input
                    value={config.mensaje_movimientos}
                    onChange={(e) => setConfig({ ...config, mensaje_movimientos: e.target.value })}
                    placeholder="Los movimientos de @periodo están listos..."
                  />
                </div>
                
                <div>
                  <Label>Mensaje: Padrón Completo</Label>
                  <Input
                    value={config.mensaje_padron}
                    onChange={(e) => setConfig({ ...config, mensaje_padron: e.target.value })}
                    placeholder="El padrón de @periodo está actualizado..."
                  />
                </div>
                
                <div>
                  <Label>Mensaje: Período Completo</Label>
                  <Input
                    value={config.mensaje_completo}
                    onChange={(e) => setConfig({ ...config, mensaje_completo: e.target.value })}
                    placeholder="El período @periodo está completo..."
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSaveConfig} style={{ backgroundColor: '#2563eb', color: 'white' }}>
                <CheckCircle size={16} className="mr-2" />
                Guardar Configuración
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* Tab: Permisos */}
        <TabsContent value="permisos">
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Permisos para Cerrar Períodos</h2>
            <p className="text-sm text-slate-500 mb-4">
              Además de Administradores, estos usuarios pueden marcar períodos como completos:
            </p>
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead className="text-center">Permiso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.filter(u => u.rol !== 'ADMINISTRADOR').map((usuario, idx) => {
                  const tienePermiso = permisos.some(p => p.user_id === usuario.id);
                  return (
                    <TableRow key={idx}>
                      <TableCell>{usuario.nombre || usuario.email}</TableCell>
                      <TableCell className="text-sm text-slate-500">{usuario.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{usuario.rol}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={tienePermiso}
                          onCheckedChange={() => handleTogglePermiso(usuario.id, tienePermiso)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
